package com.htmlagent.site.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.shared.session.SessionSummaryResponse
import com.htmlagent.site.components.widgets.TipSide
import com.htmlagent.site.components.widgets.Tooltip
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.HistoryCancelButtonVariant
import com.htmlagent.site.theme.HistoryConfirmButtonVariant
import com.htmlagent.site.theme.HistoryConfirmDetailStyle
import com.htmlagent.site.theme.HistoryConfirmStyle
import com.htmlagent.site.theme.HistoryConfirmTitleStyle
import com.htmlagent.site.theme.HistoryContentStyle
import com.htmlagent.site.theme.HistoryDeleteVariant
import com.htmlagent.site.theme.HistoryHeadingStyle
import com.htmlagent.site.theme.HistoryItemActiveStyle
import com.htmlagent.site.theme.HistoryItemMetaStyle
import com.htmlagent.site.theme.HistoryItemTitleStyle
import com.htmlagent.site.theme.HistoryItemVariant
import com.htmlagent.site.theme.HistoryPanelClosedStyle
import com.htmlagent.site.theme.HistoryPanelStyle
import com.htmlagent.site.theme.PlainHeadingStyle
import com.htmlagent.site.theme.HistoryRowStyle
import com.htmlagent.site.theme.IdentityBodyStyle
import com.htmlagent.site.theme.IdentityButtonVariant
import com.htmlagent.site.theme.IdentityCardStyle
import com.htmlagent.site.theme.IdentityInputVariant
import com.htmlagent.site.theme.IdentityTitleStyle
import com.htmlagent.site.theme.PanelCloseVariant
import com.htmlagent.site.theme.Space
import com.htmlagent.site.theme.Tokens
import com.htmlagent.site.theme.current
import com.htmlagent.site.theme.labelled
import com.htmlagent.site.theme.named
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.icons.ChevronLeftIcon
import com.varabyte.kobweb.silk.components.icons.TrashIcon
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.htmlagent.site.components.widgets.copyToClipboard
import com.htmlagent.site.state.Identity
import kotlinx.coroutines.launch
import androidx.compose.runtime.rememberCoroutineScope
import com.htmlagent.site.components.sections.landing.PRODUCT_NAME
import com.htmlagent.site.components.widgets.Mark
import com.htmlagent.site.state.rememberNarrowScreen
import com.htmlagent.site.theme.DrawerBrandStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.LandingMarkNameStyle
import com.htmlagent.site.theme.LandingMarkStyle
import com.htmlagent.site.theme.LandingMarkTileStyle
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text

/**
 * The user's previous sessions.
 *
 * Each one is named after its first request, which is the only thing the person will recognize
 * it by; the version count tells a session that got somewhere from an abandoned one.
 *
 * On a phone there is no rail, and this drawer is the whole menu: the mark and a close button at
 * the top, starting a session, the sessions, and the rail's switches at the foot.
 */
@Composable
@Suppress("LongParameterList") // On a phone the drawer carries the rail's actions as well as the sessions.
fun HistoryPanel(
    open: Boolean,
    sessions: List<SessionSummaryResponse>,
    currentId: String?,
    identity: String,
    onOpen: (String) -> Unit,
    onDelete: (String) -> Unit,
    onCollapse: () -> Unit,
    onClaim: suspend (String) -> String?,
    onNewSession: () -> Unit,
    onNewGuidedSession: () -> Unit,
    onSwitchUser: () -> Unit,
) {
    val t = LocalStrings.current
    val narrow = rememberNarrowScreen()

    // Only one row asking at a time: two invite confirming the wrong one.
    var confirming by remember { mutableStateOf<String?>(null) }

    Column(
        modifier = HistoryPanelStyle.toModifier()
            .then(if (open) Modifier else HistoryPanelClosedStyle.toModifier())
            .attrsModifier {
                attr("role", "complementary")
                attr("aria-label", t.history.panelLabel)
                if (!open) {
                    // `aria-hidden` alone was not enough, and the gap is the kind that only
                    // shows up with a keyboard: it hides the panel from a screen reader while
                    // leaving all forty-odd of its controls in the tab order, so tabbing through
                    // the app fell into a run of invisible buttons -and hidden content that can
                    // still take focus is itself an ARIA error. `inert` is the one attribute
                    // that does both: out of the tab order and out of the accessibility tree.
                    attr("inert", "")
                    attr("aria-hidden", "true")
                }
            },
    ) {
      Column(
        modifier = HistoryContentStyle.toModifier(),
        verticalArrangement = Arrangement.spacedBy(Space.Xs),
      ) {
        if (narrow) {
            Row(modifier = DrawerBrandStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
                A(
                    href = "/",
                    attrs = LandingMarkStyle.toModifier().toAttrs { attr("aria-label", t.rail.backToLanding) },
                ) {
                    Span(LandingMarkTileStyle.toModifier().toAttrs()) { Mark() }
                    Span(LandingMarkNameStyle.toModifier().toAttrs()) { Text(PRODUCT_NAME) }
                }
                Button(
                    onClick = { onCollapse() },
                    modifier = Modifier.named(t.landing.navCloseMenu),
                    variant = PanelCloseVariant,
                ) {
                    Glyph(Icons.Close, size = Tokens.iconSm.value())
                }
            }
            DrawerStart(onBlank = onNewSession, onGuided = onNewGuidedSession)
        }

        Row(
            modifier = HistoryHeadingStyle.toModifier(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            H2(PlainHeadingStyle.toModifier().toAttrs()) {
                Text(if (narrow) "${t.history.heading} · ${sessions.size}" else t.history.heading)
            }
            Spacer()
            // On a phone the drawer has its own close button at the top.
            if (!narrow) Tooltip(t.history.collapse, TipSide.BottomEnd) {
                Button(
                    onClick = { onCollapse() },
                    modifier = Modifier.named(t.history.collapse),
                    variant = PanelCloseVariant,
                    size = ButtonSize.XS,
                ) {
                    ChevronLeftIcon(Modifier.size(Tokens.iconSm.value()))
                }
            }
        }

        if (sessions.isEmpty()) {
            SpanText(t.history.emptyState, HistoryItemMetaStyle.toModifier())
        }

        sessions.forEach { summary ->
            if (confirming == summary.sessionId) {
                Confirm(
                    title = summary.title ?: t.history.untitled,
                    versions = summary.versionCount,
                    onConfirm = {
                        confirming = null
                        onDelete(summary.sessionId)
                    },
                    onCancel = { confirming = null },
                )
            } else {
                SessionRow(
                    summary = summary,
                    active = summary.sessionId == currentId,
                    touch = narrow,
                    onOpen = { onOpen(summary.sessionId) },
                    onAskDelete = { confirming = summary.sessionId },
                )
            }
        }

        // On a phone the account at the foot already says which address this is; a guest still
        // needs the card, because it is where the sessions stop living only in this browser.
        if (!narrow || Identity.isGuest(identity)) Identified(identity = identity, onClaim = onClaim)

        if (narrow) DrawerAccount(identity = identity, onSwitchUser = onSwitchUser)
      }
    }
}

/**
 * Where these pages live, and how to stop them living only here.
 *
 * A guest owns their sessions through a random key held in this browser: losing it loses them,
 * so the block offers the two ways out -copy the key, or ask for an access link, which moves the
 * sessions to that address once it is opened here. Someone who already typed one only gets told which, because for them
 * there is nothing left to decide.
 */
@Composable
private fun Identified(identity: String, onClaim: suspend (String) -> String?) {
    val t = LocalStrings.current
    val scope = rememberCoroutineScope()
    var draft by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var copied by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }

    if (!Identity.isGuest(identity)) {
        Column(IdentityCardStyle.toModifier()) {
            SpanText(t.history.identitySavedAs(identity), IdentityBodyStyle.toModifier())
        }
        return
    }

    fun save() {
        val email = draft.trim()
        if (!Identity.looksLikeEmail(email)) {
            error = t.gate.invalidEmail
            return
        }
        error = null
        saving = true
        scope.launch {
            error = onClaim(email)
            saving = false
        }
    }

    Column(
        modifier = IdentityCardStyle.toModifier(),
        verticalArrangement = Arrangement.spacedBy(Space.Sm),
    ) {
        Column {
            SpanText(t.history.identityGuestTitle, IdentityTitleStyle.toModifier())
            SpanText(t.history.identityGuestBody, IdentityBodyStyle.toModifier())
        }

        Button(
            onClick = {
                // The label is the only feedback there is, so it changes when the copy actually
                // happened and not when the click did.
                copyToClipboard(Identity.recoveryLink(identity)) { done -> copied = done }
            },
            variant = IdentityButtonVariant,
            size = ButtonSize.XS,
        ) {
            SpanText(if (copied) t.history.identityCopied else t.history.identityCopyKey)
        }

        TextInput(
            text = draft,
            onTextChange = { draft = it },
            modifier = Modifier.attrsModifier {
                attr("type", "email")
                attr("aria-label", t.gate.emailLabel)
                attr("autocomplete", "email")
                if (error != null) attr("aria-invalid", "true")
            },
            variant = IdentityInputVariant,
            placeholder = t.gate.emailPlaceholder,
            focusBorderColor = null,
        )

        error?.let { message ->
            Box(HistoryItemMetaStyle.toModifier().attrsModifier { attr("role", "alert") }) {
                SpanText(message)
            }
        }

        Button(
            onClick = { save() },
            enabled = !saving,
            variant = IdentityButtonVariant,
            size = ButtonSize.XS,
        ) {
            SpanText(if (saving) t.history.identitySaving else t.history.identitySave)
        }
    }
}

/**
 * A history row, with its delete button visible only on hover.
 *
 * Hover is kept in state and not in CSS: a descendant selector would have to target the class
 * Silk generates (see `HistoryRowStyle`). The delete button keeps the native tooltip instead of
 * [Tooltip]: it sits `position: absolute` inside the row and the wrapper `Box` would take away
 * its anchor.
 */
@Composable
private fun SessionRow(
    summary: SessionSummaryResponse,
    active: Boolean,
    touch: Boolean,
    onOpen: () -> Unit,
    onAskDelete: () -> Unit,
) {
    val t = LocalStrings.current
    var hovered by remember { mutableStateOf(false) }

    Box(
        HistoryRowStyle.toModifier()
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false },
    ) {
        Button(
            onClick = { onOpen() },
            modifier = (if (active) HistoryItemActiveStyle.toModifier() else Modifier)
                .current(active),
            variant = HistoryItemVariant,
        ) {
            Column(Modifier.fillMaxWidth()) {
                SpanText(summary.title ?: t.history.untitled, HistoryItemTitleStyle.toModifier())
                SpanText(t.history.versionCount(summary.versionCount), HistoryItemMetaStyle.toModifier())
            }
        }

        Button(
            onClick = { onAskDelete() },
            modifier = Modifier
                .labelled(t.history.deleteLabel(summary.title))
                // A phone has no hover: waiting for one left the delete button invisible there.
                .opacity(if (hovered || touch) 1.0 else 0.0),
            variant = HistoryDeleteVariant,
            size = ButtonSize.XS,
        ) {
            TrashIcon(Modifier.size(Tokens.iconSm.value()))
        }
    }
}

/**
 * The delete confirmation, in the gap left by the row itself.
 *
 * Not a browser `confirm()`: it would block the page and with it the stream. It says what is
 * lost and how much instead of "are you sure?", and the deleting button is filled so it can be
 * told apart without reading the labels.
 */
@Composable
private fun Confirm(
    title: String,
    versions: Int,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalStrings.current

    Column(
        modifier = HistoryConfirmStyle.toModifier().attrsModifier {
            attr("role", "alertdialog")
            attr("aria-label", t.history.confirmLabel(title))
        },
        verticalArrangement = Arrangement.spacedBy(Space.Md),
    ) {
        Column {
            SpanText(title, HistoryConfirmTitleStyle.toModifier())
            SpanText(t.history.deleteDetail(versions), HistoryConfirmDetailStyle.toModifier())
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(Space.Sm),
        ) {
            // Cancel first: for anything irreversible, the way outcomes first on the finger's path.
            Button(onClick = { onCancel() }, variant = HistoryCancelButtonVariant, size = ButtonSize.SM) {
                SpanText(t.history.cancel)
            }
            Button(onClick = { onConfirm() }, variant = HistoryConfirmButtonVariant, size = ButtonSize.SM) {
                SpanText(t.history.delete)
            }
        }
    }
}
